import { useMemo } from "react";

import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import { useGetFeatureSubscriptionsQuery } from "services/paywall";

const useAmbientScribeTrialStatus = () => {
  // An explicit `limit`: the endpoint pages 5 rows newest-first by default, so a practice with
  // newer subscriptions to other features read its lapsed scribe trial as never started, and the
  // purchase screen offered a free trial over a checkout that charges at once (SN-19868).
  const { data: featureSubscriptions, isLoading: isLoadingSubscriptions } =
    useGetFeatureSubscriptionsQuery({ limit: 100 });

  const isTrialExpired = useMemo(() => {
    return Boolean(
      featureSubscriptions?.some(
        (sub: any) =>
          sub.feature.feature_key === AMBIENT_SCRIBE_FEATURE_KEY &&
          Boolean(sub.trial_end) &&
          new Date(sub.trial_end) < new Date()
      )
    );
  }, [featureSubscriptions]);

  /**
   * A trial without a card, running or lapsed: no licences and no Stripe subscription. It mirrors
   * the backend's `skip_trial` rule, so after one the card checkout charges at once (SN-19868).
   */
  const hasHadNoCardTrial = useMemo(() => {
    return Boolean(
      featureSubscriptions?.some(
        (sub) =>
          sub.feature.feature_key === AMBIENT_SCRIBE_FEATURE_KEY &&
          sub.number_of_licenses === 0 &&
          !sub.stripe_subscription_id &&
          Boolean(sub.trial_end)
      )
    );
  }, [featureSubscriptions]);

  /**
   * `isLoading` (first load), NOT `isFetching`. This query is unfiltered and
   * practice-wide, so it refetches on unrelated invalidations — gating the purchase
   * placeholder on `isFetching` blanked the whole screen each time.
   */
  return { isTrialExpired, hasHadNoCardTrial, isLoadingSubscriptions };
};

export default useAmbientScribeTrialStatus;
